import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  guardarAcreditacionesPromocion,
  guardarResultadosCursoPromocion,
  listarResultadosPromocion,
  obtenerLogResultadoPromocion,
} from "../../../../services/promocionesService";
import { showToast } from "../../../../utils/showToast";
import { usePermissions } from "../../../../utils/permissions";

const EXAMEN_ESTADO_OPTIONS = [
  { value: "no_presentado", label: "No presentado" },
  { value: "primera_oportunidad", label: "1ra oportunidad" },
  { value: "segunda_oportunidad", label: "2da oportunidad" },
];

const RESULTADO_CURSO_LABELS = {
  pendiente: "Pendiente",
  aprobado: "Aprobado",
  reprobado: "Reprobado",
};

const NOTA_MIN = 0;
const NOTA_MAX = 10;

const normalizeNota = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const parseNota = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return Number.NaN;
  if (numberValue < NOTA_MIN || numberValue > NOTA_MAX) return Number.NaN;
  return numberValue;
};

const buildOriginalMap = (list = []) => {
  const base = {};
  list.forEach((row) => {
    base[row.estudiante_formacion_id] = {
      nota_curso: row.nota_curso,
      resultado_curso: row.resultado_curso,
      nota_examen: row.nota_examen,
      examen_estado: row.examen_estado,
      acreditado: row.acreditado,
    };
  });
  return base;
};

const resolveEstadoLabel = (row) => {
  if (row.acreditado) return "Acreditado";
  if (row.resultado_curso === "aprobado") return "Egresado";
  if (row.resultado_curso === "reprobado") return "Reprobado";
  return "Pendiente";
};

const resolveResultadoCursoLabel = (value) =>
  RESULTADO_CURSO_LABELS[value] || "Pendiente";

const getResultadoBadgeClass = (value) => {
  switch (value) {
    case "aprobado":
      return "bg-success text-white";
    case "reprobado":
      return "bg-danger text-white";
    default:
      return "bg-light text-dark border";
  }
};

const computeAcreditado = ({ nota_examen, examen_estado, resultado_curso, requiere_examen }) => {
  if (!requiere_examen) {
    return resultado_curso === "aprobado";
  }
  const nota = parseNota(nota_examen);
  if (nota === null || Number.isNaN(nota)) return false;
  const presentado =
    examen_estado === "primera_oportunidad" || examen_estado === "segunda_oportunidad";
  return nota >= 7 && presentado && resultado_curso === "aprobado";
};

const puedeRegistrarExamen = (row) => {
  if (!row.requiere_examen) return false;
  const notaCurso = parseNota(row.nota_curso);
  return row.resultado_curso === "aprobado" && notaCurso !== null && !Number.isNaN(notaCurso);
};

const formatNotaDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toFixed(2);
};

const buildNombre = (row) => {
  const nombre = [row.apellidos, row.nombres].filter(Boolean).join(" ").trim();
  return nombre || `Estudiante #${row.estudiante_id || "-"}`;
};

const sortByApellidoNombre = (list = []) =>
  [...list].sort((a, b) => {
    const apellidosA = String(a?.apellidos || "").trim();
    const apellidosB = String(b?.apellidos || "").trim();
    const apellidoCompare = apellidosA.localeCompare(apellidosB, "es", { sensitivity: "base" });
    if (apellidoCompare !== 0) return apellidoCompare;
    const nombresA = String(a?.nombres || "").trim();
    const nombresB = String(b?.nombres || "").trim();
    return nombresA.localeCompare(nombresB, "es", { sensitivity: "base" });
  });

export default function PromocionNotasTab({
  promocionId,
  horarios,
  horariosLoading,
  horarioFiltroId,
  setHorarioFiltroId,
  onDirtyChange,
}) {
  const { hasPermission } = usePermissions();
  const canEditarNotas = hasPermission("promociones.actualizar");
  const canCorregirNotas = hasPermission("promociones.notas.correccion");
  const [resultados, setResultados] = useState([]);
  const [resultadosLoading, setResultadosLoading] = useState(false);
  const [resultadosError, setResultadosError] = useState("");
  const [savingAll, setSavingAll] = useState(false);
  const [originales, setOriginales] = useState({});
  const [logCache, setLogCache] = useState({});
  const [openLogId, setOpenLogId] = useState(null);
  const [logPosition, setLogPosition] = useState(null);

  const loadResultados = useCallback(async () => {
    if (!promocionId) return;
    setResultadosLoading(true);
    setResultadosError("");
    try {
      const data = await listarResultadosPromocion(promocionId, {
        horarioId: horarioFiltroId || undefined,
      });
      const list = Array.isArray(data) ? data : [];
      const normalized = list.map((row) => ({
        ...row,
        nota_curso: normalizeNota(row.nota_curso),
        nota_examen: normalizeNota(row.nota_examen),
        resultado_curso: row.resultado_curso || "pendiente",
        examen_estado: row.examen_estado || "no_presentado",
        acreditado: row.acreditado === 1 || row.acreditado === true,
        requiere_examen: row.requiere_examen === 1 || row.requiere_examen === true,
      }));
      setResultados(sortByApellidoNombre(normalized));
      setOriginales(buildOriginalMap(normalized));
      setLogCache({});
      setOpenLogId(null);
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudieron cargar las notas.";
      setResultadosError(message);
      showToast({ type: "danger", message });
    } finally {
      setResultadosLoading(false);
    }
  }, [promocionId, horarioFiltroId]);

  useEffect(() => {
    loadResultados();
  }, [loadResultados]);

  const handleNotaCursoChange = useCallback(
    (rowId, value) => {
      const parsed = parseNota(value);
      if (parsed !== null && Number.isNaN(parsed)) {
        showToast({
          type: "warning",
          message: `La nota debe estar entre ${NOTA_MIN} y ${NOTA_MAX}.`,
        });
        return;
      }
      setResultados((prev) =>
        prev.map((row) => {
          if (row.estudiante_formacion_id !== rowId) return row;
          let nextResultado = row.resultado_curso;
          if (parsed === null) {
            nextResultado = "pendiente";
          } else if (!Number.isNaN(parsed)) {
            nextResultado = parsed >= 7 ? "aprobado" : "reprobado";
          }
          const nextAcreditado = computeAcreditado({
            nota_examen: row.nota_examen,
            examen_estado: row.examen_estado,
            resultado_curso: nextResultado,
            requiere_examen: row.requiere_examen,
          });
          return {
            ...row,
            nota_curso: value,
            resultado_curso: nextResultado,
            acreditado: nextAcreditado,
          };
        })
      );
    },
    []
  );

  const handleNotaExamenChange = useCallback(
    (rowId, value) => {
      const parsed = parseNota(value);
      if (parsed !== null && Number.isNaN(parsed)) {
        showToast({
          type: "warning",
          message: `La nota debe estar entre ${NOTA_MIN} y ${NOTA_MAX}.`,
        });
        return;
      }
      setResultados((prev) =>
        prev.map((row) => {
          if (row.estudiante_formacion_id !== rowId) return row;
          if (!puedeRegistrarExamen(row)) return row;
          let nextEstado = row.examen_estado;
          let nextAcreditado = row.acreditado;

          if (parsed !== null && !Number.isNaN(parsed) && nextEstado === "no_presentado") {
            nextEstado = "primera_oportunidad";
          }

          nextAcreditado = computeAcreditado({
            nota_examen: value,
            examen_estado: nextEstado,
            resultado_curso: row.resultado_curso,
            requiere_examen: row.requiere_examen,
          });

          return {
            ...row,
            nota_examen: value,
            examen_estado: nextEstado,
            acreditado: nextAcreditado,
          };
        })
      );
    },
    []
  );

  const handleExamenEstadoChange = useCallback(
    (rowId, value) => {
      if (value === "segunda_oportunidad" && !canCorregirNotas) return;
      setResultados((prev) =>
        prev.map((row) => {
          if (row.estudiante_formacion_id !== rowId) return row;
          if (value !== "no_presentado" && !puedeRegistrarExamen(row)) return row;
          let nextNota = row.nota_examen;
          let nextAcreditado = row.acreditado;

          if (value === "no_presentado") {
            nextNota = "";
            nextAcreditado = false;
          } else {
            nextAcreditado = computeAcreditado({
              nota_examen: nextNota,
              examen_estado: value,
              resultado_curso: row.resultado_curso,
              requiere_examen: row.requiere_examen,
            });
          }

          return {
            ...row,
            examen_estado: value,
            nota_examen: nextNota,
            acreditado: nextAcreditado,
          };
        })
      );
    },
    [canCorregirNotas]
  );

  const fetchLog = useCallback(
    async (rowId) => {
      setLogCache((prev) => {
        const existing = prev[rowId];
        if (existing?.loading || existing?.data) return prev;
        return {
          ...prev,
          [rowId]: { loading: true, error: "", data: null },
        };
      });

      try {
        const data = await obtenerLogResultadoPromocion(promocionId, rowId);
        setLogCache((prev) => ({
          ...prev,
          [rowId]: { loading: false, error: "", data },
        }));
      } catch (err) {
        console.error(err);
        const message = err.message || "No se pudo cargar el historial.";
        setLogCache((prev) => ({
          ...prev,
          [rowId]: { loading: false, error: message, data: null },
        }));
      }
    },
    [promocionId]
  );

  const toggleLog = useCallback(
    (rowId, event) => {
      const rect = event?.currentTarget?.getBoundingClientRect();
      setOpenLogId((prev) => {
        const next = prev === rowId ? null : rowId;
        if (next) {
          fetchLog(rowId);
          if (rect) {
            setLogPosition({
              top: rect.top + rect.height / 2,
              left: rect.right + 8,
            });
          }
        } else {
          setLogPosition(null);
        }
        return next;
      });
    },
    [fetchLog]
  );

  const isRowDirty = useCallback(
    (row) => {
      const original = originales[row.estudiante_formacion_id];
      if (!original) return true;
      return (
        original.nota_curso !== row.nota_curso ||
        original.resultado_curso !== row.resultado_curso ||
        original.nota_examen !== row.nota_examen ||
        original.examen_estado !== row.examen_estado ||
        original.acreditado !== row.acreditado
      );
    },
    [originales]
  );

  const hasChanges = useMemo(
    () => resultados.some((row) => isRowDirty(row)),
    [resultados, isRowDirty]
  );

  useEffect(() => {
    if (typeof onDirtyChange === "function") onDirtyChange(hasChanges);
  }, [hasChanges, onDirtyChange]);

  const handleReload = useCallback(() => {
    if (hasChanges) {
      const ok = window.confirm("Hay cambios sin guardar. Quieres descartarlos y actualizar?");
      if (!ok) return;
    }
    loadResultados();
  }, [hasChanges, loadResultados]);

  const handleSaveAll = useCallback(async () => {
    if (!canEditarNotas) return;
    const dirtyRows = resultados.filter((row) => isRowDirty(row));

    if (!dirtyRows.length) {
      showToast({ type: "info", message: "No hay cambios para guardar." });
      return;
    }

    const cursoPayload = [];
    const acreditacionPayload = [];
    const errores = [];

    dirtyRows.forEach((row) => {
      const original = originales[row.estudiante_formacion_id] || {};
      const cursoRegistrado =
        original.nota_curso !== null &&
        original.nota_curso !== "" &&
        original.nota_curso !== undefined;
      const examenRegistrado =
        original.examen_estado && original.examen_estado !== "no_presentado";
      const notaExamenRegistrada =
        original.nota_examen !== null &&
        original.nota_examen !== "" &&
        original.nota_examen !== undefined;

      const notaCurso = parseNota(row.nota_curso);
      let notaExamen = parseNota(row.nota_examen);
      if (row.examen_estado === "no_presentado") {
        notaExamen = null;
      }

      if (Number.isNaN(notaCurso)) {
        errores.push(`La nota de curso debe estar entre ${NOTA_MIN} y ${NOTA_MAX} para ${buildNombre(row)}.`);
      }
      if (Number.isNaN(notaExamen)) {
        errores.push(`La nota de examen debe estar entre ${NOTA_MIN} y ${NOTA_MAX} para ${buildNombre(row)}.`);
      }

      const cursoChanged =
        original.nota_curso !== row.nota_curso ||
        original.resultado_curso !== row.resultado_curso;
      const acreditadoCalc = computeAcreditado({
        nota_examen: row.nota_examen,
        examen_estado: row.examen_estado,
        resultado_curso: row.resultado_curso,
        requiere_examen: row.requiere_examen,
      });
      const acreditacionChanged =
        original.nota_examen !== row.nota_examen ||
        original.examen_estado !== row.examen_estado ||
        original.acreditado !== acreditadoCalc;

      if (cursoChanged && cursoRegistrado && !canCorregirNotas) {
        errores.push(`No tienes permiso para corregir nota de curso (${buildNombre(row)}).`);
      }
      if (
        row.examen_estado === "segunda_oportunidad" &&
        original.examen_estado !== "segunda_oportunidad" &&
        !canCorregirNotas
      ) {
        errores.push(`No tienes permiso para registrar segunda oportunidad (${buildNombre(row)}).`);
      }
      if (examenRegistrado && original.examen_estado !== row.examen_estado && !canCorregirNotas) {
        errores.push(`No tienes permiso para corregir estado de examen (${buildNombre(row)}).`);
      }
      if ((notaExamenRegistrada || original.acreditado) && acreditacionChanged && !canCorregirNotas) {
        errores.push(`No tienes permiso para corregir nota de examen (${buildNombre(row)}).`);
      }

      if (cursoChanged) {
        cursoPayload.push({
          estudiante_formacion_id: row.estudiante_formacion_id,
          nota_curso: notaCurso,
          resultado_curso: row.resultado_curso || "pendiente",
        });
      }
      if (acreditacionChanged) {
        acreditacionPayload.push({
          estudiante_formacion_id: row.estudiante_formacion_id,
          examen_estado: row.examen_estado || "no_presentado",
          nota_examen: notaExamen,
          acreditado: acreditadoCalc,
        });
      }
    });

    if (errores.length) {
      showToast({ type: "danger", message: errores[0] });
      return;
    }

    setSavingAll(true);
    try {
      if (cursoPayload.length) {
        await guardarResultadosCursoPromocion(promocionId, cursoPayload);
      }
      if (acreditacionPayload.length) {
        await guardarAcreditacionesPromocion(promocionId, acreditacionPayload);
      }
      showToast({ type: "success", message: "Notas guardadas correctamente." });
      await loadResultados();
    } catch (err) {
      console.error(err);
      showToast({
        type: "danger",
        message: err.message || "No se pudieron guardar los cambios.",
      });
    } finally {
      setSavingAll(false);
    }
  }, [canEditarNotas, canCorregirNotas, resultados, originales, isRowDirty, promocionId, loadResultados]);

  return (
    <div className="management-card promociones-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
        <div>
          <p className="management-label mb-1">Notas</p>
          <h5 className="mb-0">Asignacion de notas</h5>
          <small className="text-muted">
            {canEditarNotas ? "Recuerda guardar los cambios." : "Solo lectura. Sin permiso para editar."}
          </small>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="input-group input-group-sm">
            <span className="input-group-text">Horario</span>
            <select
              className="form-select"
              value={horarioFiltroId}
              onChange={(e) => setHorarioFiltroId(e.target.value)}
              disabled={horariosLoading || !horarios.length}
            >
              <option value="">Todos</option>
              {horarios.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.nombre}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleReload}
            disabled={resultadosLoading || savingAll}
          >
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSaveAll}
            disabled={!canEditarNotas || savingAll || !hasChanges}
          >
            <i className="bi bi-save me-2" />
            {savingAll ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>

      {resultadosError && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between management-alert mb-4">
          <div className="me-3">Notas: {resultadosError}</div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadResultados}>
            Reintentar
          </button>
        </div>
      )}

      {resultadosLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : (
        <div className="management-table-wrapper">
          <table className="table align-middle management-table promociones-table promociones-notas-table mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Matricula</th>
                <th>Estudiante</th>
                <th>Documento</th>
                <th>Nota curso</th>
                <th>Resultado curso</th>
                <th>Estado examen</th>
                <th>Nota examen</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {resultados.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-3">
                    No hay resultados disponibles para esta promocion.
                  </td>
                </tr>
              ) : (
                resultados.map((row, idx) => {
                  const estadoLabel = resolveEstadoLabel(row);
                  const resultadoLabel = resolveResultadoCursoLabel(row.resultado_curso);
                  const resultadoBadgeClass = getResultadoBadgeClass(row.resultado_curso);
                  const examenPresentado = row.examen_estado !== "no_presentado";
                  const original = originales[row.estudiante_formacion_id] || {};
                  const cursoRegistrado =
                    original.nota_curso !== null &&
                    original.nota_curso !== "" &&
                    original.nota_curso !== undefined;
                  const examenRegistrado =
                    original.examen_estado && original.examen_estado !== "no_presentado";
                  const notaExamenRegistrada =
                    original.nota_examen !== null &&
                    original.nota_examen !== "" &&
                    original.nota_examen !== undefined;
                  const examenHabilitado = puedeRegistrarExamen(row);
                  const canEditCurso = canEditarNotas && (!cursoRegistrado || canCorregirNotas);
                  const canEditExamenEstado = canEditarNotas && (!examenRegistrado || canCorregirNotas);
                  const canEditNotaExamen =
                    canEditarNotas &&
                    examenHabilitado &&
                    examenPresentado &&
                    (!notaExamenRegistrada || canCorregirNotas);
                  return (
                    <tr key={row.estudiante_formacion_id || row.matricula_id || idx}>
                      <td>{idx + 1}</td>
                      <td>{row.codigo_matricula || "-"}</td>
                      <td className="fw-semibold">{buildNombre(row)}</td>
                      <td>{row.documento_identidad || "-"}</td>
                      <td>
                        <input
                          type="number"
                          min={NOTA_MIN}
                          max={NOTA_MAX}
                          step="0.01"
                          className="form-control form-control-sm promociones-nota-input"
                          value={row.nota_curso}
                          onChange={(e) => handleNotaCursoChange(row.estudiante_formacion_id, e.target.value)}
                          disabled={!canEditCurso || savingAll}
                        />
                      </td>
                      <td>
                        <span className={`badge rounded-pill px-3 py-2 ${resultadoBadgeClass}`}>
                          {resultadoLabel}
                        </span>
                      </td>
                      <td>
                        {!row.requiere_examen ? (
                          <span className="badge rounded-pill px-3 py-2 bg-light text-dark border">
                            No aplica
                          </span>
                        ) : (
                          <select
                            className="form-select form-select-sm promociones-resultado-select"
                            value={row.examen_estado}
                            onChange={(e) =>
                              handleExamenEstadoChange(row.estudiante_formacion_id, e.target.value)
                            }
                            disabled={!canEditExamenEstado || savingAll}
                          >
                            {EXAMEN_ESTADO_OPTIONS.map((opt) => (
                              <option
                                key={opt.value}
                                value={opt.value}
                                disabled={
                                  (!examenHabilitado && opt.value !== "no_presentado") ||
                                  (!canCorregirNotas && opt.value === "segunda_oportunidad")
                                }
                              >
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        {!row.requiere_examen ? (
                          <span className="text-muted">-</span>
                        ) : (
                        <div className="promociones-nota-examen-cell">
                          <input
                            type="number"
                            min={NOTA_MIN}
                            max={NOTA_MAX}
                            step="0.01"
                            className="form-control form-control-sm promociones-nota-input"
                            value={row.nota_examen}
                            onChange={(e) =>
                              handleNotaExamenChange(row.estudiante_formacion_id, e.target.value)
                            }
                            disabled={!canEditNotaExamen || savingAll}
                          />
                          {row.examen_estado === "segunda_oportunidad" && (
                            <div className="promociones-log-wrapper">
                              <button
                                type="button"
                                className="btn btn-link p-0 promociones-log-btn"
                                onClick={(event) => toggleLog(row.estudiante_formacion_id, event)}
                                aria-label="Ver historial de examen"
                              >
                                <i className="bi bi-info-circle" />
                              </button>
                              {openLogId === row.estudiante_formacion_id && (
                                <div
                                  className="promociones-log-tooltip"
                                  style={
                                    logPosition
                                      ? {
                                          top: logPosition.top,
                                          left: logPosition.left,
                                        }
                                      : undefined
                                  }
                                >
                                  {logCache[row.estudiante_formacion_id]?.loading && (
                                    <div>Consultando historial...</div>
                                  )}
                                  {logCache[row.estudiante_formacion_id]?.error && (
                                    <div>No se pudo cargar el historial.</div>
                                  )}
                                  {!logCache[row.estudiante_formacion_id]?.loading &&
                                    !logCache[row.estudiante_formacion_id]?.error && (
                                      <div>
                                        1ra oportunidad:{" "}
                                        {formatNotaDisplay(
                                          logCache[row.estudiante_formacion_id]?.data?.primera_nota
                                        )}
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        )}
                      </td>
                      <td>
                        <span className="badge rounded-pill px-3 py-2 bg-light text-dark border">
                          {estadoLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

